/**
 * Simkl's PIN (device code) flow.
 *
 * The app shows a short code, the user types it at simkl.com/pin on any device,
 * and polling returns a token. No client secret is involved, which is what makes
 * a browser-only app possible.
 *
 * Observed against the live API on 2026-09-17:
 *   GET /oauth/pin?client_id=…          -> {user_code, expires_in: 900, interval: 5}
 *   GET /oauth/pin/{code}?client_id=…   -> {"result":"KO","message":"Authorization pending"}
 *                                       -> {"result":"OK","access_token":"…"} once authorized
 *   missing client_id                   -> 412 client_id_failed
 *
 * The trap: an unrecognised code does NOT error. Simkl falls through and issues
 * a brand-new PIN, which also carries `result: "OK"`. Treating `result === "OK"`
 * as success therefore reports a signed-in state with no token. Success is
 * defined here as the presence of an access_token, never the result field.
 */
import { SimklError } from "@/api/errors";

export interface PinRequest {
  userCode: string;
  verificationUrl: string;
  expiresInSeconds: number;
  intervalSeconds: number;
}

export class PinExpiredError extends Error {
  constructor(code: string) {
    super(`PIN ${code} expired before it was entered`);
    this.name = "PinExpiredError";
  }
}

const DEFAULT_EXPIRY_SECONDS = 900;
const DEFAULT_INTERVAL_SECONDS = 5;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

export interface PinTransport {
  request: (path: string, options?: { query?: Record<string, string> }) => Promise<unknown>;
}

/** Asks Simkl for a code to display. */
export async function requestPin(transport: PinTransport): Promise<PinRequest> {
  const body = await transport.request("/oauth/pin");
  if (!isRecord(body) || typeof body["user_code"] !== "string") {
    throw new SimklError("Simkl did not return a PIN code", 0, "/oauth/pin");
  }
  const url = body["verification_url"];
  const expires = body["expires_in"];
  const interval = body["interval"];
  return {
    userCode: body["user_code"],
    verificationUrl: typeof url === "string" ? url : "https://simkl.com/pin",
    expiresInSeconds: typeof expires === "number" ? expires : DEFAULT_EXPIRY_SECONDS,
    intervalSeconds: typeof interval === "number" ? interval : DEFAULT_INTERVAL_SECONDS,
  };
}

/**
 * One poll. Returns the token once authorized, or null while still pending.
 *
 * Deliberately keys on access_token rather than the result field — see the
 * fall-through trap in this module's header.
 */
export async function checkPin(transport: PinTransport, userCode: string): Promise<string | null> {
  const body = await transport.request(`/oauth/pin/${encodeURIComponent(userCode)}`);
  if (!isRecord(body)) return null;
  const token = body["access_token"];
  return typeof token === "string" && token.length > 0 ? token : null;
}

export interface PollOptions {
  signal?: AbortSignal;
  sleep?: (ms: number) => Promise<void>;
  now?: () => number;
}

/** Polls until the user enters the code, the code expires, or the caller aborts. */
export async function pollForToken(
  transport: PinTransport,
  pin: PinRequest,
  options: PollOptions = {},
): Promise<string> {
  const sleep =
    options.sleep ??
    ((ms: number) =>
      new Promise<void>((resolve) => {
        setTimeout(resolve, ms);
      }));
  const now = options.now ?? Date.now;
  const deadline = now() + pin.expiresInSeconds * 1000;

  while (now() < deadline) {
    options.signal?.throwIfAborted();
    const token = await checkPin(transport, pin.userCode);
    if (token !== null) return token;
    await sleep(pin.intervalSeconds * 1000);
  }
  throw new PinExpiredError(pin.userCode);
}
